import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { CalendarDays, FileText, StickyNote, Upload, Trash2, Eye, IndianRupee, CalendarClock } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import { format } from "date-fns";

import { GroupedSlotGrid } from "../components/GroupedSlotGrid";

const statusStyle = {
  pending: "bg-[#E8DAB2]/70 text-amber-900",
  confirmed: "bg-[#D9E4E1] text-[#2f5448]",
  cancelled: "bg-red-100 text-red-700",
  completed: "bg-[#2C557E]/10 text-[#2C557E]",
};

const payLabel = {
  unpaid: "Payment pending",
  paid: "Paid",
  pay_at_clinic: "Fee payable at the Clinic/Hospital",
};

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function PatientDashboard() {
  const { user, loginWith } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [records, setRecords] = useState([]);
  const [notes, setNotes] = useState([]);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [abhaOpen, setAbhaOpen] = useState(false);
  const [abhaInput, setAbhaInput] = useState("");
  const [abhaSaving, setAbhaSaving] = useState(false);
  const [rescheduleAppt, setRescheduleAppt] = useState(null);
  const [rsDate, setRsDate] = useState(null);
  const [rsSlot, setRsSlot] = useState(null);
  const [rsAvail, setRsAvail] = useState(null);
  const [rsSaving, setRsSaving] = useState(false);
  const [payEnabled, setPayEnabled] = useState(false);

  useEffect(() => {
    api.get("/payments/config").then(({ data }) => setPayEnabled(!!data.online_payment_enabled)).catch(() => {});
  }, []);

  const refresh = useCallback(() => {
    api.get("/appointments/my").then(({ data }) => setAppointments(data)).catch(() => {});
    api.get("/records/my").then(({ data }) => setRecords(data)).catch(() => {});
    api.get("/notes/my").then(({ data }) => setNotes(data)).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!rescheduleAppt || !rsDate) return;
    setRsSlot(null);
    setRsAvail(null);
    api
      .get("/availability", { params: { date: format(rsDate, "yyyy-MM-dd"), type: rescheduleAppt.type } })
      .then(({ data }) => setRsAvail(data))
      .catch((e) => toast.error(apiError(e)));
  }, [rsDate, rescheduleAppt]);

  const openReschedule = (appt) => {
    setRsDate(null);
    setRsSlot(null);
    setRsAvail(null);
    setRescheduleAppt(appt);
  };

  const submitReschedule = async () => {
    setRsSaving(true);
    try {
      await api.post(`/appointments/${rescheduleAppt.id}/reschedule`, {
        date: format(rsDate, "yyyy-MM-dd"),
        slot: rsSlot,
      });
      toast.success("Appointment rescheduled — a confirmation email is on its way");
      setRescheduleAppt(null);
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRsSaving(false);
    }
  };

  const payOnline = async (appt) => {
    if (appt.type !== "online") return;
    try {
      const { data } = await api.post("/payments/create-order", { appointment_id: appt.id });
      const ok = await loadRazorpayScript();
      if (!ok) return toast.error("Could not load payment gateway. Please try again.");
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Dr. Prathusha K — Clinic",
        description: appt.service_name,
        order_id: data.order_id,
        prefill: { name: data.name, email: data.email, contact: data.contact },
        theme: { color: "#5E8B7E" },
        handler: async (res) => {
          try {
            await api.post("/payments/verify", {
              appointment_id: appt.id,
              order_id: data.order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("Payment successful!");
            refresh();
          } catch (e) {
            toast.error(apiError(e));
          }
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const cancelAppt = async (appt) => {
    try {
      await api.post(`/appointments/${appt.id}/cancel`);
      toast.success("Appointment cancelled");
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please choose a file (PDF, PNG or JPG, max 5 MB)");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      await api.post("/records/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded securely");
      setFile(null);
      setTitle("");
      document.getElementById("record-file-input").value = "";
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const viewRecord = async (rec) => {
    try {
      const { data } = await api.get(`/records/file/${rec.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteRecord = async (rec) => {
    try {
      await api.delete(`/records/${rec.id}`);
      toast.success("Record deleted");
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveAbha = async () => {
    setAbhaSaving(true);
    try {
      const { data } = await api.post("/auth/abha", { abha_id: abhaInput });
      loginWith(data);
      toast.success("ABHA ID saved to your profile");
      setAbhaOpen(false);
      setAbhaInput("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAbhaSaving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="patient-dashboard">
      <h1 className="font-heading text-3xl md:text-4xl text-slate-900">
        Hello, {user?.name?.split(" ")[0]}
      </h1>
      <p className="text-slate-500 text-sm mt-1">
        Your secure patient portal — appointments, payments, documents and medical history in one place.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {user?.unique_id && (
          <Badge data-testid="patient-unique-id" className="bg-[#2C557E]/10 text-[#2C557E] hover:bg-[#2C557E]/10 rounded-full px-4 py-1.5 text-sm font-semibold tracking-wide">
            Patient ID: {user.unique_id}
          </Badge>
        )}
        {user?.abha_id ? (
          <Badge data-testid="patient-abha-id" variant="outline" className="rounded-full px-4 py-1.5 text-sm">
            ABHA: {user.abha_id.replace(/^ABHA/, "")}
          </Badge>
        ) : (
          <button
            data-testid="add-abha-btn"
            onClick={() => setAbhaOpen(true)}
            className="text-sm text-[#2C557E] font-medium hover:underline"
          >
            + Add your ABHA ID
          </button>
        )}
      </div>

      <Dialog open={abhaOpen} onOpenChange={setAbhaOpen}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="abha-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add your ABHA ID</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Enter your 14-digit Ayushman Bharat Health Account (ABHA) number. It will be saved
              securely on your patient file.
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="abha-input"
            inputMode="numeric"
            value={abhaInput}
            onChange={(e) => setAbhaInput(e.target.value)}
            placeholder="14-digit ABHA number"
            className="rounded-xl"
          />
          <Button
            data-testid="abha-save-btn"
            disabled={abhaSaving || abhaInput.replace(/\D/g, "").length !== 14}
            onClick={saveAbha}
            className="w-full rounded-full bg-[#5E8B7E] hover:bg-[#4A7065]"
          >
            {abhaSaving ? "Saving…" : "Save ABHA ID"}
          </Button>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="appointments" className="mt-8">
        <TabsList className="bg-[#F4F7F6] rounded-full p-1">
          <TabsTrigger data-testid="tab-appointments" value="appointments" className="rounded-full data-[state=active]:bg-[#D9E4E1] data-[state=active]:text-slate-900">
            <CalendarDays className="w-4 h-4 mr-1.5" /> Appointments
          </TabsTrigger>
          <TabsTrigger data-testid="tab-records" value="records" className="rounded-full data-[state=active]:bg-[#E8DAB2] data-[state=active]:text-slate-900">
            <FileText className="w-4 h-4 mr-1.5" /> My Documents
          </TabsTrigger>
          <TabsTrigger data-testid="tab-notes" value="notes" className="rounded-full data-[state=active]:bg-[#2C557E]/15 data-[state=active]:text-[#2C557E]">
            <StickyNote className="w-4 h-4 mr-1.5" /> Doctor's Notes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="appointments" className="mt-6 space-y-4">
          {appointments.length === 0 && (
            <div className="text-center py-14 bg-white rounded-2xl border border-dashed border-[#D9E4E1] text-slate-400 text-sm">
              No appointments yet.{" "}
              <a href="/booking" className="text-[#2C557E] font-medium hover:underline">Book your first visit</a>
            </div>
          )}
          {appointments.map((a) => (
            <div key={a.id} data-testid={`appointment-card-${a.id}`} className="bg-white rounded-2xl border border-[#EDF1EF] p-5 flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-heading font-semibold text-slate-800">{a.service_name}</span>
                  <Badge className={`${statusStyle[a.status]} rounded-full capitalize hover:${statusStyle[a.status]}`} data-testid={`appointment-status-${a.id}`}>
                    {a.status}
                  </Badge>
                  <Badge variant="outline" className="rounded-full">
                    {a.type === "online" ? "Online" : a.location || "In-Clinic"}
                  </Badge>
                </div>
                <div className="text-sm text-slate-500 mt-1.5">
                  {a.date} at {a.slot} · ₹{a.fee} ·{" "}
                  <span className={a.payment_status === "paid" ? "text-[#4A7065] font-medium" : ""}>
                    {payLabel[a.payment_status] || a.payment_status}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {(a.status === "pending" || a.status === "confirmed") && (
                  <Button data-testid={`reschedule-btn-${a.id}`} size="sm" variant="outline" onClick={() => openReschedule(a)} className="rounded-full">
                    <CalendarClock className="w-3.5 h-3.5 mr-1" /> Reschedule
                  </Button>
                )}
                {a.type === "online" && a.status === "confirmed" && a.payment_status !== "paid" && (
                  payEnabled ? (
                    <Button data-testid={`pay-online-btn-${a.id}`} size="sm" onClick={() => payOnline(a)} className="rounded-full bg-[#2C557E] hover:bg-[#22436a]">
                      <IndianRupee className="w-3.5 h-3.5 mr-1" /> Pay ₹{a.fee} Online
                    </Button>
                  ) : (
                    <span className="text-xs text-slate-500 self-center" data-testid={`online-fee-note-${a.id}`}>
                      (₹{a.fee} fee — the clinic team will contact you for payment)
                    </span>
                  )
                )}
                {a.type === "clinic" && a.status === "confirmed" && (
                  <span className="text-xs text-slate-500 self-center" data-testid={`clinic-fee-note-${a.id}`}>
                    (Consultation fee will be received only by the Clinic/Hospital)
                  </span>
                )}
                {(a.status === "pending" || (a.status === "confirmed" && a.payment_status !== "paid")) && (
                  <Button data-testid={`cancel-appt-btn-${a.id}`} size="sm" variant="ghost" onClick={() => cancelAppt(a)} className="rounded-full text-red-500 hover:text-red-600 hover:bg-red-50">
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          ))}

          <Dialog open={!!rescheduleAppt} onOpenChange={(o) => !o && setRescheduleAppt(null)}>
            <DialogContent className="max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="reschedule-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading">Reschedule appointment</DialogTitle>
              </DialogHeader>
              {rescheduleAppt && (
                <div>
                  <p className="text-sm text-slate-500">
                    {rescheduleAppt.service_name} — currently on <b>{rescheduleAppt.date}</b> at{" "}
                    <b>{rescheduleAppt.slot}</b>. Pick a new time on the same date, or a different date.
                  </p>
                  <div className="flex justify-center mt-3 bg-[#F4F7F6] rounded-2xl p-2" data-testid="reschedule-calendar">
                    <Calendar
                      mode="single"
                      selected={rsDate}
                      onSelect={setRsDate}
                      disabled={(d) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const max = new Date(today);
                        max.setDate(max.getDate() + 60);
                        if (d < today || d > max) return true;
                        if (rescheduleAppt.type === "online" && d.getTime() === today.getTime()) return true;
                        if (rescheduleAppt.type === "clinic" && d.getDay() === 0) return true;
                        return false;
                      }}
                    />
                  </div>
                  {rsDate && !rsAvail && (
                    <div className="flex justify-center py-4">
                      <div className="w-5 h-5 border-4 border-[#5E8B7E] border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                  {rsAvail && (
                    <div className="mt-4" data-testid="reschedule-slots">
                      <GroupedSlotGrid
                        slots={rsAvail.slots}
                        selected={rsSlot}
                        onSelect={setRsSlot}
                        testIdPrefix="reschedule-slot"
                        gridClass="grid grid-cols-4 gap-2"
                      />
                    </div>
                  )}
                  <Button
                    data-testid="reschedule-confirm-btn"
                    disabled={!rsDate || !rsSlot || rsSaving}
                    onClick={submitReschedule}
                    className="w-full mt-5 rounded-full bg-[#5E8B7E] hover:bg-[#4A7065]"
                  >
                    {rsSaving ? "Rescheduling…" : "Confirm New Slot"}
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="records" className="mt-6">
          <form onSubmit={upload} className="bg-white rounded-2xl border border-[#EDF1EF] p-5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
            <div className="flex-1">
              <label className="text-sm font-medium text-slate-700 block mb-1.5">Document title</label>
              <Input data-testid="record-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Ultrasound report — May 2026" className="rounded-xl" />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium text-slate-700 block mb-1.5">File (PDF / JPG / PNG, max 5 MB)</label>
              <Input id="record-file-input" data-testid="record-file-input" type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setFile(e.target.files[0])} className="rounded-xl" />
            </div>
            <Button data-testid="record-upload-btn" type="submit" disabled={uploading} className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065]">
              <Upload className="w-4 h-4 mr-1.5" /> {uploading ? "Uploading…" : "Upload"}
            </Button>
          </form>
          <p className="text-xs text-slate-400 mt-2">
            Documents are stored securely and visible only to you and Dr. Prathusha.
          </p>
          <div className="mt-5 space-y-3">
            {records.length === 0 && (
              <div className="text-center py-10 bg-white rounded-2xl border border-dashed border-[#D9E4E1] text-slate-400 text-sm">
                No documents uploaded yet.
              </div>
            )}
            {records.map((r) => (
              <div key={r.id} data-testid={`record-item-${r.id}`} className="bg-white rounded-2xl border border-[#EDF1EF] p-4 flex items-center gap-4">
                <span className="w-10 h-10 rounded-xl bg-[#E8DAB2]/60 flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-slate-700" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800 text-sm truncate">{r.title}</div>
                  <div className="text-xs text-slate-400">
                    {r.original_name} · {(r.size / 1024).toFixed(0)} KB · {new Date(r.uploaded_at).toLocaleDateString()}
                  </div>
                </div>
                <Button data-testid={`record-view-btn-${r.id}`} size="sm" variant="outline" onClick={() => viewRecord(r)} className="rounded-full">
                  <Eye className="w-3.5 h-3.5" />
                </Button>
                <Button data-testid={`record-delete-btn-${r.id}`} size="sm" variant="ghost" onClick={() => deleteRecord(r)} className="rounded-full text-red-500 hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="notes" className="mt-6 space-y-3">
          {notes.length === 0 && (
            <div className="text-center py-10 bg-white rounded-2xl border border-dashed border-[#D9E4E1] text-slate-400 text-sm">
              No notes from the doctor yet. After your visits, Dr. Prathusha's notes and advice will appear here.
            </div>
          )}
          {notes.map((n) => (
            <div key={n.id} className="bg-white rounded-2xl border-l-4 border-[#2C557E] border border-[#EDF1EF] p-5">
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{n.note}</p>
              <p className="text-xs text-slate-400 mt-2">
                {n.created_by} · {new Date(n.created_at).toLocaleString()}
              </p>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
